/**
 * CacheStorage abstraction behind ResultCache.
 *
 *   LocalStorageCacheStorage → browser (persists across reloads); used for the
 *                              price service market lists (lib/prices)
 */

export interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export interface CacheStorage {
  getEntry<T>(key: string): CacheEntry<T> | null;
  setEntry<T>(key: string, entry: CacheEntry<T>): void;
  removeEntry(key: string): void;
}

function isLocalStorageAvailable(): boolean {
  try {
    return typeof localStorage !== "undefined";
  } catch {
    return false;
  }
}

export class LocalStorageCacheStorage implements CacheStorage {
  constructor(private readonly namespace: string = "mds") {}

  private prefixed(key: string): string {
    return `${this.namespace}:${key}`;
  }

  getEntry<T>(key: string): CacheEntry<T> | null {
    if (!isLocalStorageAvailable()) return null;
    try {
      const raw = localStorage.getItem(this.prefixed(key));
      if (!raw) return null;
      return JSON.parse(raw) as CacheEntry<T>;
    } catch {
      return null;
    }
  }

  setEntry<T>(key: string, entry: CacheEntry<T>): void {
    if (!isLocalStorageAvailable()) return;
    try {
      localStorage.setItem(this.prefixed(key), JSON.stringify(entry));
    } catch {
      // Quota exceeded — silently skip persistence
    }
  }

  removeEntry(key: string): void {
    if (!isLocalStorageAvailable()) return;
    try {
      localStorage.removeItem(this.prefixed(key));
    } catch {
      /* ignore */
    }
  }

  /**
   * Drop expired rows under the given key prefixes so old symbols do not
   * linger in localStorage forever when never read again.
   */
  sweepExpired(ttlMs: number, subPrefixes: string[]): void {
    if (!isLocalStorageAvailable()) return;
    const nsPrefix = `${this.namespace}:`;
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey?.startsWith(nsPrefix)) continue;

      const relative = fullKey.slice(nsPrefix.length);
      if (!subPrefixes.some((p) => relative.startsWith(p))) continue;

      try {
        const raw = localStorage.getItem(fullKey);
        if (!raw) {
          keysToRemove.push(fullKey);
          continue;
        }
        const parsed = JSON.parse(raw) as { fetchedAt?: unknown };
        const fetchedAt = parsed.fetchedAt;
        if (
          typeof fetchedAt !== "number" ||
          Number.isNaN(fetchedAt) ||
          Date.now() - fetchedAt > ttlMs
        ) {
          keysToRemove.push(fullKey);
        }
      } catch {
        keysToRemove.push(fullKey);
      }
    }

    for (const k of keysToRemove) {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    }
  }
}
